import React, { useState, useEffect, useMemo } from 'react';
import { CalendarProps } from '@/types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const Calendar: React.FC<CalendarProps> = React.memo(({
  value,
  onChange,
  onClose,
  showMonthYearDropdown = false
}) => {
  const [calendarMonth, setCalendarMonth] = useState(value?.getMonth() || new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(value?.getFullYear() || new Date().getFullYear());

  // Update calendar when value changes
  useEffect(() => {
    if (value) {
      setCalendarMonth(value.getMonth());
      setCalendarYear(value.getFullYear());
    }
  }, [value]);

  // Generate calendar days
  const generateCalendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    // CRITICAL FIX: Use startDate directly and increment by days to handle month overflow correctly
    // The previous approach (startDate.getDate() + i) could overflow when crossing month boundaries
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  }, [calendarMonth, calendarYear]);

  // Navigation functions
  const changeMonth = (offset: number) => {
    let newMonth = calendarMonth + offset;
    let newYear = calendarYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    setCalendarMonth(newMonth);
    setCalendarYear(newYear);
  };

  const handleDateClick = (date: Date) => {
    // Create a new date at local midnight to avoid timezone issues
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    onChange?.(localDate);
    onClose?.();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === calendarMonth && date.getFullYear() === calendarYear;
  };

  const isSelected = (date: Date) => {
    return value && date.toDateString() === value.toDateString();
  };

  const isToday = (date: Date) => {
    return date.toDateString() === new Date().toDateString();
  };

  // [calendar-fix] Handle Today button click
  const handleTodayClick = () => {
    const today = new Date();
    onChange?.(today);
    onClose?.();
  };

  return (
    <div className="rounded-lg shadow-lg border theme-border p-4 w-80 bg-[var(--bg-elev)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => changeMonth(-1)}
          className="p-2 hover:theme-muted rounded-full transition-colors"
        >
          <svg className="w-4 h-4 theme-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex items-center space-x-2">
          {showMonthYearDropdown ? (
            <>
              <select
                value={calendarMonth}
                onChange={(e) => setCalendarMonth(parseInt(e.target.value))}
                className="text-sm font-medium bg-[var(--bg-elev)] border-none focus:outline-none theme-text"
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index} className="theme-text">{month}</option>
                ))}
              </select>
              <svg className="w-4 h-4 theme-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              <select
                value={calendarYear}
                onChange={(e) => setCalendarYear(parseInt(e.target.value))}
                className="text-sm font-medium bg-[var(--bg-elev)] border-none focus:outline-none theme-text"
              >
                {Array.from({ length: 10 }, (_, i) => calendarYear - 5 + i).map(year => (
                  <option key={year} value={year} className="theme-text">{year}</option>
                ))}
              </select>
              <svg className="w-4 h-4 theme-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          ) : (
            <h2 className="text-lg font-semibold theme-text">
              {MONTHS[calendarMonth]} {calendarYear}
            </h2>
          )}
        </div>

        <button
          onClick={() => changeMonth(1)}
          className="p-2 hover:theme-muted rounded-full transition-colors"
        >
          <svg className="w-4 h-4 theme-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS.map(day => (
          <div key={day} className="text-center text-xs font-medium theme-text-muted py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid - Hide trailing/leading days from other months */}
      <div className="grid grid-cols-7 gap-1 mb-3">
        {generateCalendarDays.map((date, index) => {
          const isCurrentMonthDay = isCurrentMonth(date);
          const isSelectedDay = isSelected(date);
          const isTodayDay = isToday(date);

          // Hide days from other months (trailing/leading days)
          if (!isCurrentMonthDay) {
            return (
              <div key={index} className="w-8 h-8" /> // Empty placeholder to maintain grid
            );
          }

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              className={`
                w-8 h-8 text-sm rounded-full transition-colors theme-text
                ${isSelectedDay ? 'bg-[var(--accent-primary)] text-white border border-[color-mix(in_srgb,var(--accent-primary) 60%, transparent)]' : 'hover:bg-[color-mix(in_srgb,var(--accent-primary) 10%, var(--bg) 90%)]'}
                ${isTodayDay && !isSelectedDay ? 'bg-red-500 text-white' : ''}
              `}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Today button in footer */}
      <div className="flex justify-end pt-2 border-t theme-border">
        <button
          onClick={handleTodayClick}
          className="px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
        >
          Today
        </button>
      </div>
    </div>
  );
});

Calendar.displayName = 'Calendar';

export default Calendar; 